import { OTAChannel } from '@/types/revenue-manager';
import { delay, API_ENDPOINTS, apiClient, ApiResponse, USE_MOCK } from './client';
import { mockOTAChannelsData } from '@/data/mock-data';

export interface OTAChannelsResponse {
  success: boolean;
  data: OTAChannel[];
  total: number;
}

// ============ API Functions ============

export async function fetchOTAChannels(branchId?: string | number): Promise<OTAChannelsResponse> {
  if (USE_MOCK) {
    await delay(200);
    return {
      success: true,
      data: mockOTAChannelsData,
      total: mockOTAChannelsData.length,
    };
  }
  
  // Use cm/ota/connectedList endpoint with pms_property_id filter
  const filters = JSON.stringify({
    pms_property_id: branchId || '0'
  });
  
  const response = await apiClient.get<any>(
    `/cm/ota/connectedList?filters=${encodeURIComponent(filters)}`
  );
  
  // Extract data from response
  const rawOtaChannelsData = response.data?.data || [];
  
  // Debug: Log raw data to see actual field names
  // if (rawOtaChannelsData.length > 0) {
  //   console.log('OTA Channel Raw Data Sample:', rawOtaChannelsData[0]);
  // }
  
  // Transform API data to match OTAChannel interface
  const otaChannelsData = rawOtaChannelsData.map((channel: any, index: number) => {
    const commission = Number(channel.commission || channel.commission_rate || 0);
    const description = channel.description || channel.channel_description || '';
    const channelName = channel.name || channel.channel_name || channel.ota_name || 'Unknown Channel';
    const channelCode = channel.ota_code || 'Unknown_Channel';
    
    // Try multiple ID fields, fallback to name or index
    const channelId = channel.id || channel.channel_id || channel.ota_id || channel.cm_id || 
                      channelName.toLowerCase().replace(/\s+/g, '-') || `channel-${index}`;
    
    // console.log(`OTA Channel: "${channelName}" -> ID: "${channelId}"`);
    
    return {
      id: String(channelId),
      name: channelName,
      code: channelCode,
      ...(commission > 0 && { commission }),
      ...(description && { description })
    };
  });
  
  return {
    success: response.data.success,
    data: otaChannelsData,
    total: otaChannelsData.length,
  };
}

export async function fetchOTAChannelById(channelId: string): Promise<ApiResponse<OTAChannel | null>> {
  if (USE_MOCK) {
    await delay(200);
    const channel = mockOTAChannelsData.find(c => c.id === channelId);
    return {
      success: !!channel,
      data: channel || null,
    };
  }
  
  const response = await apiClient.get<ApiResponse<OTAChannel | null>>(
    API_ENDPOINTS.otaChannels.get(channelId)
  );
  return response.data;
}

export async function createOTAChannel(
  channel: Omit<OTAChannel, 'id'>
): Promise<ApiResponse<OTAChannel>> {
  if (USE_MOCK) {
    await delay();
    const newChannel: OTAChannel = {
      ...channel,
      id: `ota-${Date.now()}`,
    };
    mockOTAChannelsData.push(newChannel);
    
    return {
      success: true,
      data: newChannel,
    };
  }
  
  const response = await apiClient.post<ApiResponse<OTAChannel>>(
    API_ENDPOINTS.otaChannels.create,
    channel
  );
  return response.data;
}

export async function updateOTAChannel(
  channelId: string,
  updates: Partial<OTAChannel>
): Promise<ApiResponse<OTAChannel | null>> {
  if (USE_MOCK) {
    await delay();
    const index = mockOTAChannelsData.findIndex(c => c.id === channelId);
    
    if (index === -1) {
      return { success: false, data: null };
    }
    
    mockOTAChannelsData[index] = { ...mockOTAChannelsData[index], ...updates };
    return {
      success: true,
      data: mockOTAChannelsData[index],
    };
  }
  
  const response = await apiClient.patch<ApiResponse<OTAChannel | null>>(
    API_ENDPOINTS.otaChannels.update(channelId),
    updates
  );
  return response.data;
}

export async function deleteOTAChannel(channelId: string): Promise<ApiResponse<null>> {
  if (USE_MOCK) {
    await delay();
    const index = mockOTAChannelsData.findIndex(c => c.id === channelId);
    
    if (index !== -1) {
      mockOTAChannelsData.splice(index, 1);
    }
    return { success: true, data: null };
  }
  
  const response = await apiClient.delete<ApiResponse<null>>(
    API_ENDPOINTS.otaChannels.delete(channelId)
  );
  return response.data;
}
